/*
Sets a focus trap to keep focus within given parent elements.
If multiple parent elements are provided, tabbing will cycle between the parent element's children,
  even if they are not consecutive in the page's tab order.
*/

export function setFocusTrap(
  event: KeyboardEvent,
  elements: (HTMLElement | undefined)[]
) {
  if (event.key === 'Tab') {
    const firstAndLast = getFirstAndLastFocusableChildren(elements)
    if (firstAndLast.length === 0) return

    if (event.shiftKey) {
      /* shift + tab */
      const containerIndex = firstAndLast.findIndex(
        (focusableElements) =>
          focusableElements.first === document.activeElement
      )
      if (containerIndex !== -1) {
        let newIndex = containerIndex - 1
        if (containerIndex === 0) {
          newIndex = firstAndLast.length - 1
        }
        firstAndLast[newIndex].last.focus()
        event.preventDefault()
      }
    } else {
      /* tab */
      const containerIndex = firstAndLast.findIndex(
        (focusableElements) => focusableElements.last === document.activeElement
      )
      if (containerIndex !== -1) {
        let newIndex = containerIndex + 1
        if (newIndex === firstAndLast.length) {
          newIndex = 0
        }
        firstAndLast[newIndex].first.focus()
        event.preventDefault()
      }
    }

    return true // if the event was intercepted
  } else {
    return false // if the event was left alone
  }
}

export function focusFirstChild(element: HTMLElement | undefined) {
  const firstAndLast = getFirstAndLastFocusableChildren([element])
  if (firstAndLast.length > 0 && firstAndLast[0]?.first) {
    firstAndLast[0]?.first.focus()
  }
}

const getFirstAndLastFocusableChildren = (
  elements: (HTMLElement | undefined)[]
) => {
  return elements
    .map((element) => {
      if (element) {
        // ignoring buttons with tabindex of -1 keeps the background button from being included
        const focusableElements = [
          ...element.querySelectorAll(
            'a[href]:not([disabled]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
          )
        ]

        if (focusableElements.length > 0) {
          return {
            first: focusableElements[0],
            last: focusableElements[focusableElements.length - 1]
          }
        }
      }
      return null
    })
    .filter((focusableElements) => focusableElements !== null) as {
    first: HTMLElement
    last: HTMLElement
  }[]
}
